import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BaseComponent } from '@core/base/base.component';
import {
  NotificationItem,
  NotificationType,
} from '@core/models/notification.model';
import { NotificationService } from '@core/services/notification.service';
import { getRelativeTime } from '@core/utils/date.util';
import {
  LucideBell,
  LucideCheckCheck,
  LucideCircleAlert,
  LucideCircleCheckBig,
  LucideShield,
} from '@lucide/angular';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LucideBell,
    LucideCheckCheck,
    LucideCircleAlert,
    LucideCircleCheckBig,
    LucideShield,
  ],
  templateUrl: './notification-dialog.component.html',
  styleUrl: './notification-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationDialogComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  protected readonly getRelativeTime = getRelativeTime;
  readonly activeModal = inject(NgbActiveModal);
  readonly notificationService = inject(NotificationService);

  readonly items = computed(() => this.notificationService.items());

  ngOnInit(): void {
    this.subscribeSafe(this.notificationService.loadInitial());
  }

  ngOnDestroy(): void {
    this.notificationService.reset();
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 20; // px to bottom
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <=
      threshold;

    if (
      isNearBottom &&
      !this.isBusy() &&
      !this.notificationService.isLoadingMore() &&
      this.notificationService.hasMore()
    ) {
      this.subscribeSafe(this.notificationService.loadNextPage());
    }
  }

  markAsRead(item: NotificationItem): void {
    if (!item.is_read) {
      this.subscribeSafe(
        this.notificationService.markAsRead(item.id),
        undefined,
        { silent: true },
      );
    }
  }

  markAllAsRead(): void {
    this.subscribeSafe(this.notificationService.markAllAsRead(), undefined, {
      silent: true,
    });
  }

  iconWrapperClass(type: NotificationType): string {
    switch (type) {
      case 'alert':
        return 'bg-warning-subtle text-warning';
      case 'success':
        return 'bg-success-subtle text-success';
      case 'security':
        return 'bg-info-subtle text-info';
      default:
        return 'bg-primary-subtle text-primary';
    }
  }
}
